import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../AdminLayout.tsx';
import { adminFetch, createUserApiKey } from '../../../utils/adminFetch.ts';

interface UserData {
  _id: string;
  email: string;
  name?: string;
  googleId?: string;
  credits: number;
  createdAt?: string;
  lastLogin?: string;
}

interface UserFile {
  id: string;
  filename: string;
  fileType: string;
  uploadDate: string;
}

const UserEdit: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userFiles, setUserFiles] = useState<UserFile[]>([]);
  const [apiKey, setApiKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingKey, setIsGeneratingKey] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) return;

      setIsLoading(true);
      setError(null);

      try {
        const data = await adminFetch<UserData>(`/users/${userId}`);
        setUserData(data);

        const filesResponse = await adminFetch<{ files: UserFile[] }>(`/users/${userId}/files`);
        setUserFiles(filesResponse.files || []);
      } catch (err: any) {
        console.error('Error fetching user:', err.message);
        setError(err.message || 'Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchUser();
  }, [userId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    if (!userData) return;

    const { name, value, type } = e.target as HTMLInputElement;

    setUserData(prev => {
      if (!prev) return null;
      return {
        ...prev,
        [name]: type === 'checkbox'
          ? (e.target as HTMLInputElement).checked
          : type === 'number'
            ? Number(value)
            : value
      };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData || !userId) return;

    setIsSubmitting(true);
    setError(null);

    try {
      await adminFetch(`/users/${userId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      navigate('/admin/users');
    } catch (err: any) {
      console.error('Error updating user:', err.message);
      setError(err.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateApiKey = async () => {
    if (!userId) return;

    setIsGeneratingKey(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await createUserApiKey(userId);
      setApiKey(response.key);
      setSuccessMessage('New API key generated successfully');
    } catch (err: any) {
      console.error('Error creating API key:', err.message);
      setError(err.message || 'Failed to create API key');
    } finally {
      setIsGeneratingKey(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title="Edit User">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!userData) {
    return (
      <AdminLayout title="Edit User">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <p>User not found</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={`Edit User: ${userData.email}`}>
      <div className="max-w-4xl mx-auto">
        {error && (
          <div className="mb-6 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
            <p>{successMessage}</p>
          </div>
        )}

        <div className="bg-base rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold font-space-grotesk mb-4">User Information</h2>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-dark mb-2">
                  Email Address <span className="text-red-600">*</span>
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={userData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
                />
              </div>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-dark mb-2">
                  Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={userData.name}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
                />
              </div>

              <div>
                <label htmlFor="googleId" className="block text-sm font-medium text-gray-dark mb-2">
                  Google ID
                </label>
                <input
                  id="googleId"
                  name="googleId"
                  type="text"
                  value={userData.googleId}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
                />
              </div>

              <div>
                <label htmlFor="credits" className="block text-sm font-medium text-gray-dark mb-2">
                  Credits
                </label>
                <input
                  id="credits"
                  name="credits"
                  type="number"
                  min="0"
                  value={userData.credits}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-upload-button-pressed"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate('/admin/users')}
                className="px-4 py-2 border rounded-lg hover:bg-gray-light"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-upload-button-pressed text-white rounded-lg hover:bg-upload-button-hover hover:text-black disabled:opacity-50"
              >
                {isSubmitting ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>

        <div className="bg-base rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-xl font-bold font-space-grotesk mb-4">API Key Management</h2>

          {apiKey && (
            <div className="mb-4 bg-gray-light p-4 rounded-lg">
              <p className="text-sm text-gray-dark mb-2">New API Key (Copy this, it won't be shown again):</p>
              <div className="flex items-center">
                <input
                  type="text"
                  value={apiKey}
                  readOnly
                  className="flex-1 p-2 border rounded-l-lg bg-gray-base text-sm font-mono"
                />
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(apiKey);
                    alert('API key copied to clipboard');
                  }}
                  className="bg-upload-button-pressed text-white p-2 rounded-r-lg hover:bg-upload-button-hover hover:text-black"
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          <button
            onClick={handleCreateApiKey}
            disabled={isGeneratingKey}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {isGeneratingKey ? 'Generating...' : 'Generate New API Key'}
          </button>
        </div>

        <div className="bg-base rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold font-space-grotesk mb-4">User Files</h2>

          {userFiles.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-base">
                <thead className="bg-gray-base">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Filename
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-dark uppercase tracking-wider">
                      Upload Date
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-base divide-y divide-gray-base">
                  {userFiles.map((file) => (
                    <tr key={file.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{file.filename}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{file.fileType}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {new Date(file.uploadDate).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-dark">No files found for this user.</p>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserEdit;
